/**
 * Signing in, offered rather than demanded.
 *
 * Playing without an account still works, and that is the whole distribution
 * story: a duel link sent to a stranger has to open a game, not a signup form.
 * So this sits in the quiet part of the home screen and its pitch is about
 * what an account PROTECTS, not what it unlocks. There is nothing behind it
 * that anonymous play does not already have.
 *
 * The device is what has been playing. An account attaches to it rather than
 * replacing it, so nobody loses a history by signing up, and nobody has to
 * sign up before they have one worth keeping.
 */

import { useEffect, useState } from 'react';

import { authProviders, googleSignInUrl, requestMagicLink } from '../lib/api';
import { useGame } from '../store/gameStore';
import { identityToken } from '../store/storage';

type Sent =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sending' }
  | { readonly kind: 'sent'; readonly email: string }
  | { readonly kind: 'failed'; readonly why: string };

export interface AccountPanelProps {
  /**
   * Start with the form showing.
   *
   * For the sign-in route, where the player has already said what they came
   * for. Everywhere else the panel discloses itself, because asking a stranger
   * for an address before they have found a single country is asking them to
   * sign into a game they have not played.
   */
  readonly defaultOpen?: boolean;
  /** Copy for somebody restoring an account rather than creating one. */
  readonly restoring?: boolean;
}

export function AccountPanel({
  defaultOpen = false,
  restoring = false,
}: AccountPanelProps = {}): React.JSX.Element {
  const account = useGame((s) => s.account);
  const signOut = useGame((s) => s.signOut);
  const [open, setOpen] = useState(defaultOpen);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<Sent>({ kind: 'idle' });
  const [google, setGoogle] = useState(false);

  /*
   * Asked once, on mount. A gateway with no Google credentials must not draw
   * the button at all: a sign-in option that cannot work is worse than one
   * that is absent, because the player blames themselves for the failure.
   */
  useEffect(() => {
    let live = true;
    void authProviders().then((p) => {
      if (live) setGoogle(p.google);
    });
    return () => {
      live = false;
    };
  }, []);

  if (account !== null) {
    return (
      <section className="gg-section gg-section-quiet">
        <h2 className="gg-section-subtitle">Account</h2>
        <p className="gg-note">
          Signed in as <strong>{account.email}</strong>. Your progress follows you to any device
          you sign in on.
        </p>
        {/*
          SIGNING OUT ONLY BECAME NECESSARY WHEN SIGNING IN STARTED STICKING.
          ==============================================================
          While every reload forgot you, there was nothing to leave. Now that a
          browser remembers, a shared computer needs a way to hand it back --
          otherwise the next person to sit down is holding somebody else's
          coverage, rivalries and name with no way to put them down.
        */}
        <button type="button" className="gg-account-restore" onClick={signOut}>
          Sign out of this browser
        </button>
      </section>
    );
  }

  const deviceToken = identityToken();

  /*
   * A MISSING DEVICE TOKEN IS FINE. It is minted by the socket, and the socket
   * does not open until a match starts, so a freshly installed browser has
   * none. That is the new-tablet restore -- exactly the person accounts exist
   * for -- and the gateway mints them a player rather than refusing.
   */
  const send = (): void => {
    setSent({ kind: 'sending' });
    void requestMagicLink(email.trim(), deviceToken ?? '')
      .then(() => setSent({ kind: 'sent', email: email.trim() }))
      .catch((err: unknown) =>
        setSent({
          kind: 'failed',
          why:
            (err as Error).message === 'bad_email'
              ? 'That does not look like an email address.'
              : 'Could not send that just now. Try again in a moment.',
        }),
      );
  };

  return (
    <section className="gg-section gg-section-quiet">
      <h2 className="gg-section-subtitle">Account <span className="gg-section-optional">optional</span></h2>

      {!open ? (
        <>
          <p className="gg-note">
            You are playing as a guest, and everything is saved on this device. Sign in to keep
            your progress if you lose this browser, to carry it to your phone, and to pick it up
            again on a device you have never played on.
          </p>
          <button type="button" className="gg-btn" onClick={() => setOpen(true)}>
            Save my progress
          </button>
          {/*
            THE OTHER HALF OF THE SAME BUTTON, said out loud.
            ==========================================================
            Both of these open the same form, and always did -- signing in with
            an address that already has an account restores it rather than
            making a second one. What was missing was anybody SAYING so. A
            returning player on a new phone read "Save my progress", concluded
            it was for people who did not already have an account, and had no
            way in.
          */}
          <button type="button" className="gg-account-restore" onClick={() => setOpen(true)}>
            Already have an account? Sign in
          </button>
        </>
      ) : (
        <>
          {sent.kind === 'sent' ? (
            <p className="gg-note">
              Check <strong>{sent.email}</strong>. The link works once and expires in fifteen
              minutes. You can open it on any device.
            </p>
          ) : (
            <>
              {/*
                GOOGLE LEADS WHEN GOOGLE IS AVAILABLE.
                ==============================================================
                Most players already have a Google account, and one tap beats
                typing an address, switching to a mail app and coming back. The
                magic link is the better FALLBACK -- it works for everybody and
                needs nothing set up -- but making it the primary put the
                slower path in the bigger button.

                "Link Google" rather than "Continue with Google": the standard
                phrasing is written for a sign-in wall, and this is not one.
                Nobody here is blocked -- they are already playing as a guest,
                and the offer is to attach that progress to something that
                outlives the browser. Link says what it does to what they have.
              */}
              {/*
                THE VERB DEPENDS ON WHY YOU ARE HERE.
                ==============================================================
                "Link Google" is right on the home screen, where a guest is
                attaching progress they already have to something that outlives
                the browser. It is wrong on the sign-in route, where the
                account exists and Google is already attached to it -- there is
                nothing left to link, and being offered the setup step for
                something already set up reads as though the app has not
                noticed you have an account.
              */}
              {google ? (
                <a
                  className="gg-btn gg-btn-primary"
                  href={googleSignInUrl(deviceToken ?? '')}
                >
                  {restoring ? 'Sign in with Google' : 'Link Google'}
                </a>
              ) : null}

              {google ? <p className="gg-note gg-auth-or">or</p> : null}

              <label className="gg-lobby-name">
                <span className="gg-lobby-name-label">Email</span>
                <input
                  className="gg-input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  placeholder="you@example.com"
                  onChange={(e) => setEmail(e.target.value)}
                  aria-label="Email address"
                />
              </label>
              <button
                type="button"
                className={google ? 'gg-btn' : 'gg-btn gg-btn-primary'}
                disabled={sent.kind === 'sending' || email.trim() === ''}
                onClick={send}
              >
                {sent.kind === 'sending'
                  ? 'Sending…'
                  : restoring
                    ? 'Email me a sign-in link'
                    : 'Email me a link'}
              </button>

              {sent.kind === 'failed' ? (
                <p className="gg-note gg-note-error">{sent.why}</p>
              ) : null}
            </>
          )}

          <p className="gg-note">
            {restoring
              ? 'No password. Use the address your account is on and we will send you back into it.'
              : 'No password. We use your address to sign you in and for nothing else. If you have signed in before, this puts you back into the same account.'}
          </p>
        </>
      )}
    </section>
  );
}
